/**
 * Centralized React Query keys + domain invalidation helpers.
 *
 * WHY THIS EXISTS
 * ---------------
 * Query keys were hand-typed strings scattered across ~40 files. Typos like
 * ["cafe-detail"] or ["user-journal"] (neither matches any query) silently
 * no-op'd on invalidation, and cross-cutting caches like ["map-cafes"] and the
 * cover-photo accumulator got missed by writers over and over. This module
 * centralizes the strings and, more importantly, provides *domain helpers*
 * that fan out to every cache in a family so a single call site never has to
 * remember the whole set.
 *
 * KEY INVARIANTS
 * --------------
 * 1. `undefined` in a key tuple is NOT a wildcard in TanStack Query.
 *    `["user-streak", undefined]` will never match `["user-streak", "abc"]`.
 *    Every factory below either requires its id argument or has a separate
 *    `*Prefix()` sibling that returns the shorter tuple. Never make an id
 *    argument optional.
 *
 * 2. When migrating a call site to a helper, every key it currently
 *    invalidates that the helper does NOT cover must remain as an explicit
 *    `qc.invalidateQueries` line. A migration may only ADD coverage, never
 *    remove it. See src/components/EditCheckInPhotosDialog.tsx (retains
 *    work-journal / my-photos) and src/components/PostVisitReflectionDialog.tsx
 *    (retains work-journal / recent-cafe-conditions) for examples.
 *
 * 3. No key strings are renamed here — this is a pure centralization so
 *    existing reader hooks (which still hand-type their queryKey tuples) keep
 *    hitting the same caches. Reader migration is deferred to a follow-up.
 *
 * DELIBERATELY NOT MIGRATED
 * -------------------------
 * - `src/hooks/useBadges.ts` (`checkBadges`) runs after every check-in,
 *   review, photo, and focus-session action. Swapping its invalidation block
 *   for `invalidateUserStats` would refetch the entire user-stats family on
 *   every hot-path action, on top of the calling dialog's own invalidations,
 *   for zero coverage gain. The `newlyEarned` gating on ["user-badges"] is
 *   deliberate.
 * - Narrow one-off writers (Browse, Favorites, BestCafesAdmin, OwnerReviews)
 *   whose current key set doesn't line up with any domain helper.
 * - Reader hooks (`useQuery({ queryKey: ... })`) — deferred to a follow-up.
 */

import type { QueryClient } from "@tanstack/react-query";
import { invalidateCafeCoverPhoto } from "@/hooks/useCafeCoverPhotos";

// Re-export so call sites only need to import from "@/lib/queryKeys".
// The evictor registry has to stay in useCafeCoverPhotos.ts because it's a
// module-level Set that the accumulator hook writes into on mount.
export { invalidateCafeCoverPhoto };

// -------------------------------------------------------------------------
// Key factory
// -------------------------------------------------------------------------
// Namespaced object of pure functions/constants returning the exact same
// tuples currently in use throughout the codebase. Grepped from
// `rg "queryKey:" src` — additions are safe, renames are not.
export const queryKeys = {
  // Browse / cafe lists
  cafes: () => ["cafes"] as const,
  mapCafes: () => ["map-cafes"] as const,

  /** Public homepage live café counts (per metro). */
  homeCounts: () => ["home-counts"] as const,


  cafe: {
    /** Cafe detail is keyed by SLUG, not id. Readers use this shape. */
    bySlug: (slug: string) => ["cafe", slug] as const,
    /** Prefix — invalidates every ["cafe", *] tuple. */
    prefix: () => ["cafe"] as const,
    slug: (id: string) => ["cafe-slug", id] as const,
    special: (id: string, pacificDate: string) =>
      ["cafe-special-today", id, pacificDate] as const,
    announcement: (id: string) => ["cafe-announcement", id] as const,
    specialHoursToday: (pacificDate: string) =>
      ["cafe-special-hours-today", pacificDate] as const,
  },

  // Check-ins / conditions
  checkins: {
    forCafe: (id: string) => ["cafe-checkins", id] as const,
    today: (id: string) => ["todayCheckIns", id] as const,
    recentConditions: (id: string) => ["recent-cafe-conditions", id] as const,
    recentConditionsPrefix: () => ["recent-cafe-conditions"] as const,
    historicalVibes: (id: string, dayOfWeek: number, hour: number) =>
      ["historical-vibes", id, dayOfWeek, hour] as const,
  },

  // Photos
  photos: {
    forCafe: (id: string) => ["cafe-photos", id] as const,
    counts: (id: string) => ["cafe-photo-counts", id] as const,
    all: () => ["cafe-all-photos"] as const,
    allAccum: () => ["cafe-all-photos-accum-batch"] as const,
    cover: () => ["cafe-cover-photos"] as const,
    coverBatch: () => ["cafe-cover-photos-batch"] as const,
    coverAccum: () => ["cafe-cover-photos-accum-batch"] as const,
    my: (userId: string) => ["my-photos", userId] as const,
    myCount: (userId: string) => ["my-photos-count", userId] as const,
  },

  reviews: {
    forCafe: (id: string) => ["reviews", id] as const,
  },

  feed: {
    root: () => ["feed"] as const,
    liveStats: () => ["feed-live-stats"] as const,
  },

  profile: {
    self: (userId: string) => ["profile", userId] as const,
    header: (userId: string) => ["header-profile", userId] as const,
    public: (userId: string) => ["public-profile", userId] as const,
  },

  user: {
    // Streak / badges: NEVER take an optional id — split into id + prefix
    // variants so callers can't accidentally emit ["user-streak", undefined].
    streak: (userId: string) => ["user-streak", userId] as const,
    streakPrefix: () => ["user-streak"] as const,
    badgeProgress: (userId: string) => ["badge-progress", userId] as const,
    badgeProgressPrefix: () => ["badge-progress"] as const,
    userBadges: (userId: string) => ["user-badges", userId] as const,
    weeklyRecap: (userId: string) => ["weekly-recap", userId] as const,
    weeklyRecapPrefix: () => ["weekly-recap"] as const,

    checkedInToday: (cafeId: string, userId: string) =>
      ["user-checked-in-today", cafeId, userId] as const,
    checkedInTodayPrefix: () => ["user-checked-in-today"] as const,
    contributions: (cafeId: string, userId: string) =>
      ["user-cafe-contributions", cafeId, userId] as const,
    contributionsPrefix: () => ["user-cafe-contributions"] as const,
    visits: (cafeId: string, userId: string) =>
      ["user-cafe-visits", cafeId, userId] as const,
    visitsPrefix: () => ["user-cafe-visits"] as const,
    monthCheckins: (userId: string) => ["user-month-checkins", userId] as const,
    checkins: (userId: string) => ["user-checkins", userId] as const,
    checkinsCount: (userId: string) => ["user-checkins-count", userId] as const,
    reviews: (userId: string) => ["user-reviews", userId] as const,
    reviewsCount: (userId: string) => ["user-reviews-count", userId] as const,
    favorites: (userId: string) => ["user-favorites", userId] as const,
    favoritesCount: (userId: string) => ["user-favorites-count", userId] as const,
    communityImpact: (userId: string) => ["community-impact", userId] as const,
    contributionStats: (userId: string) => ["contribution-stats", userId] as const,
    workJournal: (userId: string) => ["work-journal", userId] as const,
    verified: (userId: string) => ["verified-status", userId] as const,
    preferences: () => ["user-preferences"] as const,
  },

  admin: {
    stats: () => ["admin-stats"] as const,
    syncStatus: () => ["admin-sync-status"] as const,
    blogPages: () => ["admin-blog-pages"] as const,
    blogPage: () => ["blog-page"] as const,
    cafes: () => ["admin-cafes"] as const,
    cafePhotos: (id: string) => ["admin-cafe-photos", id] as const,
    allPhotos: () => ["admin-all-photos"] as const,
  },
} as const;

// -------------------------------------------------------------------------
// Domain invalidation helpers
// -------------------------------------------------------------------------

/**
 * Everything that renders cafe data for a given cafe id or in a browse list.
 *
 * Includes ["map-cafes"] — currently invalidated by ZERO writers in the app.
 * Every previous manual invalidation of cafe/cafes/cafe-checkins/todayCheckIns
 * missed the map. This helper fixes that.
 */
export function invalidateCafeData(qc: QueryClient, cafeId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.cafe.prefix() }); // ["cafe"] — slug-keyed detail
  qc.invalidateQueries({ queryKey: queryKeys.cafes() });
  qc.invalidateQueries({ queryKey: queryKeys.mapCafes() });
  qc.invalidateQueries({ queryKey: queryKeys.checkins.forCafe(cafeId) });
  qc.invalidateQueries({ queryKey: queryKeys.checkins.today(cafeId) });
  qc.invalidateQueries({ queryKey: queryKeys.checkins.recentConditions(cafeId) });
  qc.invalidateQueries({ queryKey: queryKeys.cafe.announcement(cafeId) });
}

/**
 * Everything that renders a cafe's photos. Also nudges the cover-photo
 * accumulator caches Browse actually renders from — those aren't reached by
 * a plain ["cafe-cover-photos"] invalidation.
 */
export function invalidateCafePhotos(qc: QueryClient, cafeId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.photos.forCafe(cafeId) });
  qc.invalidateQueries({ queryKey: queryKeys.photos.counts(cafeId) });
  qc.invalidateQueries({ queryKey: queryKeys.photos.all() });
  qc.invalidateQueries({ queryKey: queryKeys.photos.allAccum() });
  invalidateCafeCoverPhoto(qc, cafeId);
}

/**
 * The three places a user's identity renders from: their own profile page,
 * the header bubble, and any public /u/:username view.
 */
export function invalidateUserProfile(qc: QueryClient, userId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.profile.self(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.profile.header(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.profile.public(userId) });
}

/**
 * All the counters and stat rollups a user's activity affects.
 *
 * `user-reviews` and `user-reviews-count` are included so review writers can
 * swap in this helper without dropping keys the profile page depends on.
 *
 * Prefix invalidations are used for the (cafeId, userId) composite keys
 * (checked-in-today, contributions, visits) so we don't have to know every
 * cafe id — invalidating the prefix fans out to every mounted composite.
 */
export function invalidateUserStats(qc: QueryClient, userId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.user.streak(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.checkedInTodayPrefix() });
  qc.invalidateQueries({ queryKey: queryKeys.user.contributionsPrefix() });
  qc.invalidateQueries({ queryKey: queryKeys.user.visitsPrefix() });
  qc.invalidateQueries({ queryKey: queryKeys.user.monthCheckins(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.checkins(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.checkinsCount(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.reviews(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.reviewsCount(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.badgeProgress(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.communityImpact(userId) });
  qc.invalidateQueries({ queryKey: queryKeys.user.contributionStats(userId) });
}
